<script>
  // ── Abgang prüfen beim Abschluss (A7/L10) ─────────────────────────────────
  //
  // Konzept: Konzepte/Lager/abgang-pruefen.md, Abschnitte 4 und 5.
  //
  // Die Maske ERSETZT bei freigeschaltetem Lager die Rückfrage „Backtag
  // abschließen?". Sie ist das Spiegelbild der Eingangsmaske — eine Liste aus
  // Zutat und Menge, Summe und Sammelknopf in der Fußleiste — und hat deshalb
  // dieselbe Hülle: schmal ganzer Bildschirm, ab 600 px Container-Breite ein
  // Dialog.
  //
  // WAS HIER NICHT PASSIERT
  // Die Maske schreibt nichts ins Journal. Sie gibt die bestätigten Zeilen an
  // den Aufrufer, und der reicht sie über setBacktagStatus() an den Haken in
  // lager-abgang.js. Zwei Schreibwege für denselben Abgang liefen ins Rennen
  // (Konzept 5.1).
  //
  // Die Rechnung — welche Zeile geprüft werden muss, was sie bucht, welcher
  // Satz das erklärt — steht in lager-abgang.js. Hier ist Darstellung und der
  // Zustand der Eingabe (offen, Suche, Einheit, Text im Feld).
  import { onMount } from 'svelte';
  import ZutatSuche from '../teile/ZutatSuche.svelte';

  /**
   * @type {{ bt: any, vorschlag: any[], hinweis?: string|null,
   *          onbestaetigt: (abgang: any[], aliasse: any[]) => void,
   *          onabbruch: () => void }}
   */
  let { bt, vorschlag, hinweis = null, onbestaetigt, onabbruch } = $props();

  const t  = (k, p) => window.t(k, p);
  const tn = (k, n, p) => window.tn(k, n, p);
  const PRUEFEN = window.LAGER_ABSCHLUSS_PRUEFEN;
  const NICHT   = window.LAGER_ABSCHLUSS_NICHT;

  const ISTMAC = typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');

  // Genau EINMAL gelesen: Die Maske wird bei jedem Öffnen frisch montiert, und
  // die Gruppe einer Zeile steht beim Öffnen fest (lager-abgang.js).
  // svelte-ignore state_referenced_locally
  let zeilen = $state(vorschlag.map((z) => ({ ...z })));

  /** Anzeige einer Menge im Feld: ab 1 kg in kg, darunter in g — ohne Tausenderpunkt. */
  function feldWert(gramm) {
    const loc = window.currentLocale();
    if (gramm >= 1000) return { einheit: 'kg', eingabe: (gramm / 1000).toLocaleString(loc, { maximumFractionDigits: 3, useGrouping: false }) };
    return { einheit: 'g', eingabe: String(gramm) };
  }

  /** @type {Record<string, { offen: boolean, suche: boolean, einheit: string, eingabe: string }>} */
  // svelte-ignore state_referenced_locally
  let ui = $state(Object.fromEntries(vorschlag.map((z) => [z.key, {
    // ALLE Zeilen zu, auch die in „Prüfen" (Betrieb 2026-09-15): Aufgeklappt
    // stand beim ersten Abschluss so viel im Bild, dass die Maske überforderte.
    // Was geprüft werden muss, sagen Gruppe, Strich und Kopfzeile schon.
    offen: false, suche: false, ...feldWert(z.planGramm),
  }])));

  let laeuft = $state(false);

  // Ein gescheitertes Anlegen je Zeile (Konzept 10.4). Kein `notify`: Der
  // Fehler gehört an die Zeile, die ihn hat — die Maske bleibt ja offen.
  /** @type {Record<string, string>} */
  let fehler = $state({});

  // Nur die Gruppe „Nicht im Lager geführt" klappt zu, und sie startet so:
  // Was nichts bucht, soll nicht der erste Anblick sein. Die übrigen Gruppen
  // haben keinen Schalter — sie sind der Inhalt des Journals.
  let nichtAuf = $state(false);

  // ── Das Zutaten-Journal ist einklappbar (Betrieb 2026-09-15, Konzept 11) ──
  // Es startet IMMER zu, auch mit Konflikten (Betreiber, 2026-09-16). Die
  // vorige Fassung klappte bei Konflikten auf — mit dem Chip „2 Zutaten prüfen"
  // daneben war das doppelt gemoppelt: Der Chip ist deutlich genug, er zählt
  // live herunter und öffnet das Journal mit einem Griff. Aufgeklappt stand
  // stattdessen wieder die lange Liste als erster Anblick.
  //
  // Bewusst NICHT gemerkt: Ein gespeicherter Zustand versteckte beim nächsten
  // Backtag entweder die Konflikte oder zeigte grundlos alles.
  let journalAuf = $state(false);

  // Der Bestand kann sich während der Maske ändern (Cloud-Pull, zweites Gerät).
  // Die Hinweise folgen ihm; gebucht wird ohnehin relativ (Konzept 5.2).
  let takt = $state(0);
  onMount(() => {
    const neu = () => { takt++; };
    document.addEventListener('lager:journal-geaendert', neu);
    return () => document.removeEventListener('lager:journal-geaendert', neu);
  });

  let lage      = $derived.by(() => { takt; return window.lagerAbgangZeilenLage(zeilen); });
  let gruppen   = $derived(window.lagerAbgangGruppen(zeilen));
  let buchungen = $derived(window.lagerAbgangBuchungen(zeilen));
  let summe     = $derived(buchungen.reduce((s, b) => s + b.gramm, 0));
  let geaendert = $derived([...lage.values()].some((l) => l.geaendert));
  // Live: sinkt, während jemand die Zeilen repariert — das ist die Rückmeldung.
  let zuPruefen = $derived([...lage.values()].filter((l) => l.hinweise.some((h) => h.warn)).length);

  let unter = $derived.by(() => {
    const d = new Date(`${bt.datum}T12:00:00`);
    const datum = isNaN(d.getTime()) ? bt.datum
      : d.toLocaleDateString(window.currentLocale(), { weekday: 'short', day: '2-digit', month: '2-digit' });
    return bt.hinweis ? `${datum} · ${bt.hinweis}` : datum;
  });

  const menge = (g) => { const m = window.lagerMenge(g); return `${m.wert} ${m.einheit}`; };
  const zutatName = (id) => (id ? window.zutatById(id)?.name : null) || null;

  function eingeben(z, text) {
    const u = ui[z.key];
    u.eingabe = text;
    const n = parseFloat(String(text).replace(',', '.'));
    z.gramm = Number.isFinite(n) && n > 0 ? Math.round(u.einheit === 'kg' ? n * 1000 : n) : 0;
  }

  /** Die Einheit wechselt, die MENGE bleibt: Vorbelegt ist eine Zahl, kein leeres Feld. */
  function einheitWechseln(z) {
    const u = ui[z.key];
    u.einheit = u.einheit === 'kg' ? 'g' : 'kg';
    u.eingabe = z.gramm > 0
      ? (u.einheit === 'kg'
          ? (z.gramm / 1000).toLocaleString(window.currentLocale(), { maximumFractionDigits: 3, useGrouping: false })
          : String(z.gramm))
      : u.eingabe;
  }

  function waehle(z, treffer) {
    z.zutatId = treffer.zutatId;
    // Eine gewählte Zutat schlägt das Anlegen: Beides zugleich hieße, denselben
    // Abgang zweimal zu buchen.
    z.anlegen = false;
    delete fehler[z.key];
    ui[z.key].suche = false;
    // Ein Ersatz wird nie gemerkt (lagerAbgangAliasse) — das Häkchen einer
    // früheren Zuordnung darf nicht an der neuen Wahl hängen bleiben.
    if (z.planZutatId) z.merken = false;
  }

  /** „{Zeilentitel}" als Zutat anlegen — geschrieben wird erst beim Bestätigen. */
  function anlegenAn(z) {
    z.anlegen = true;
    // Kein Alias nötig: Angelegt wird unter genau diesem Titel, der nächste
    // Backtag findet die Zutat über den Namen.
    z.merken = false;
    delete fehler[z.key];
    ui[z.key].suche = false;
  }

  function fuehren(z, wert) {
    z.imLager = wert;
    if (!wert) { z.anlegen = false; z.merken = false; ui[z.key].suche = false; }
    delete fehler[z.key];
  }

  /**
   * Nimmt NUR zurück, was keinen eigenen Gegenknopf hat: getauschte Zutat,
   * geänderte Menge.
   *
   * ⚠ Nicht `imLager`, nicht `anlegen`. Die beiden haben je einen benannten
   * Gegenknopf („Doch im Lager führen", „Doch nicht anlegen"). Räumte diese
   * Rücknahme sie mit weg, stünden zwei Schaltflächen nebeneinander, die
   * dasselbe tun — und nur eine sagt, was sie tut (Betrieb 2026-09-16).
   */
  function zuruecksetzen(z) {
    z.zutatId = z.planZutatId;
    z.gramm = z.planGramm;
    z.merken = false;
    Object.assign(ui[z.key], { suche: false, ...feldWert(z.planGramm) });
  }

  /**
   * Erst schreiben, was eine Kennung braucht, dann abschließen.
   *
   * Anlegen braucht die Cloud. Scheitert es (offline in der Backstube), wird
   * NICHTS abgeschlossen: Die Maske bleibt offen, die Zeile springt auf und
   * trägt ihren Fehler (Konzept 10.4). Ein halb abgeschlossener Backtag mit
   * halb gebuchtem Abgang wäre ein Zustand, den später niemand erklären kann.
   *
   * Die Entscheidung „nicht / doch im Lager führen" ist milder: Scheitert sie,
   * bucht die Zeile trotzdem nichts — verloren ist nur die Erinnerung daran,
   * und die wird beim nächsten Abschluss wieder gefragt.
   */
  async function bestaetigen() {
    if (laeuft) return;
    laeuft = true;

    for (const z of zeilen) {
      if (!z.anlegen || z.zutatId || !z.imLager) continue;
      const neu = await window.zutatAnlegen({
        name: z.name, warengruppe: z.neuGruppe, rechenrolle: z.neuRolle,
      });
      if (!neu?.id) {
        fehler[z.key] = t('lager_abschluss_fehler_anlegen', { name: z.name });
        journalAuf = true;
        ui[z.key].offen = true;
        laeuft = false;
        return;
      }
      z.zutatId = neu.id;
      z.anlegen = false;
      delete fehler[z.key];
    }

    for (const e of window.lagerAbgangEntscheidungen($state.snapshot(zeilen))) {
      const gespeichert = await window.zutatImLagerSetzen(e, e.imLager);
      if (!gespeichert) {
        window.notify?.({ level: 'warn', dedupeKey: 'lager-abschluss-entscheidung',
                          text: t('lager_abschluss_fehler_entscheidung', { name: e.name }) });
      }
    }

    const stand = $state.snapshot(zeilen);
    onbestaetigt(window.lagerAbgangBuchungen(stand), window.lagerAbgangAliasse(stand));
  }

  // Ein Klick daneben schließt nur, solange nichts geändert wurde — sonst
  // wären die Eingaben mit einem Fehlgriff weg. Kreuz, Knopf und Escape sind
  // bewusste Wege und schließen immer.
  function schleier() { if (!geaendert) onabbruch(); }
</script>

<svelte:window onkeydown={(e) => {
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); bestaetigen(); }
  else if (e.key === 'Escape') { e.preventDefault(); onabbruch(); }
}} />

<div class="lg-host">
  <div class="lg-scrim" onclick={schleier} role="presentation"></div>
  <div class="lg-abs" role="dialog" aria-modal="true" aria-labelledby="lg-abs-titel">

    <div class="lg-abs__head">
      <div class="lg-abs__kopftext">
        <div class="lg-abs__titel" id="lg-abs-titel">{t('lager_abschluss_titel')}</div>
        <div class="lg-abs__unter">{unter}</div>
      </div>
      <button type="button" class="lg-abs__x" onclick={onabbruch} aria-label={t('lager_abschluss_abbrechen')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
             stroke-linecap="round" width="19" height="19" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>

    <div class="lg-abs__leib">
      {#if hinweis}<p class="lg-abs__hinweis">{hinweis}</p>{/if}

      <!-- Die Überleitung: „Backtag abschließen" heißt hier auch „Bestände buchen".
           Ohne diese Karte stand beim ersten Abschluss eine Zutatenliste da, ohne
           dass irgendwo stand, was mit ihr passiert (Betrieb 2026-09-15). -->
      <section class="lg-uebersicht" aria-labelledby="lg-uebersicht-titel">
        <h2 class="lg-uebersicht__titel" id="lg-uebersicht-titel">{t('lager_abschluss_karte_titel')}</h2>
        <p class="lg-uebersicht__text">{t('lager_abschluss_karte_text')}</p>
        <div class="lg-uebersicht__werte">
          <span class="lg-wert">
            {buchungen.length
              ? tn('lager_abschluss_summe', buchungen.length, { count: buchungen.length, menge: menge(summe) })
              : t('lager_abschluss_karte_nichts')}
          </span>
          {#if zuPruefen}
            <button type="button" class="lg-wert lg-wert--pruefen" onclick={() => (journalAuf = true)}>
              {tn('lager_abschluss_karte_pruefen', zuPruefen, { count: zuPruefen })}
            </button>
          {:else}
            <span class="lg-wert lg-wert--ok">{t('lager_abschluss_karte_ok')}</span>
          {/if}
        </div>
      </section>

      <button type="button" class="lg-journal-kopf" aria-expanded={journalAuf} aria-controls="lg-journal"
              onclick={() => (journalAuf = !journalAuf)}>
        <span class="lg-journal-kopf__titel">{t('lager_abschluss_journal')}</span>
        <span class="lg-journal-kopf__anzahl">{zeilen.length}</span>
        <span class="lg-journal-kopf__tu">{t(journalAuf ? 'lager_abschluss_journal_zu' : 'lager_abschluss_journal_auf')}</span>
        <svg class="lg-journal-kopf__pfeil" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" width="16" height="16" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      <div id="lg-journal" hidden={!journalAuf}>
      <p class="lg-journal-note">{t('lager_abschluss_note')}</p>
      {#each gruppen as g (g.gruppe)}
        <section class="lg-gruppe" class:is-pruefen={g.gruppe === PRUEFEN} class:is-nicht={g.gruppe === NICHT}>
          {#if g.gruppe === NICHT}
            <!-- Als einzige Gruppe ein Schalter, und sie startet zu: Was nichts
                 bucht, soll nicht der erste Anblick des Journals sein. -->
            <button type="button" class="lg-gruppe__kopf lg-gruppe__kopf--knopf"
                    aria-expanded={nichtAuf} onclick={() => (nichtAuf = !nichtAuf)}>
              <span class="lg-gruppe__name">{g.titel}</span>
              <span class="lg-gruppe__anzahl">{g.zeilen.length}</span>
              <svg class="lg-gruppe__pfeil" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" width="15" height="15" aria-hidden="true">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          {:else}
            <div class="lg-gruppe__kopf">
              <span class="lg-gruppe__name">{g.titel}</span>
              <span class="lg-gruppe__anzahl">{g.zeilen.length}</span>
            </div>
          {/if}
          <div class="lg-karte" hidden={g.gruppe === NICHT && !nichtAuf}>
            {#each g.zeilen as z (z.key)}
              {@const l = lage.get(z.key)}
              {@const u = ui[z.key]}
              {@const warn = l.hinweise.filter((h) => h.warn)}
              <!-- Zugeklappt nennt der Kopf „nicht im Lager" schon selbst — darunter
                   steht nur, was er NICHT sagt. Derselbe Satz zweimal war zu viel. -->
              {@const warnZu = warn.filter((h) => h.art !== 'offen')}
              {@const stamm = zutatName(z.zutatId)}
              <div class="lg-zeile" class:is-offen={u.offen} class:is-aus={!l.bucht} class:is-warn={warn.length > 0}>
                <button type="button" class="lg-zeile__kopf" aria-expanded={u.offen}
                        onclick={() => (u.offen = !u.offen)}>
                  <span class="lg-zeile__name">
                    {z.name}
                    {#if l.geaendert}<span class="lg-zeile__marke">{t('lager_abschluss_geaendert')}</span>{/if}
                  </span>
                  <!-- Der Lagername nur, wo er etwas SAGT: zugeklappt, und nur wenn er
                       vom Rezeptnamen abweicht. „Roggenschrot (grob) · Roggenschrot
                       (grob)" war im ersten Screenshot reines Rauschen; aufgeklappt
                       steht er ohnehin im Feld darunter. -->
                  <span class="lg-zeile__stamm" class:is-leer={z.imLager && !stamm && !z.anlegen}>
                    {#if !u.offen}
                      {#if !z.imLager}{t('lager_abschluss_hinweis_nicht')}
                      {:else if z.anlegen}{t('lager_abschluss_neu_kurz')}
                      {:else if !stamm}{t('lager_abschluss_hinweis_offen')}
                      {:else if stamm.toLowerCase() !== z.name.toLowerCase()}→ {stamm}{/if}
                    {/if}
                  </span>
                  <span class="lg-zeile__wert">{l.bucht ? menge(z.gramm) : '—'}</span>
                  <svg class="lg-zeile__pfeil" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" width="16" height="16" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>

                {#if !u.offen && warnZu.length}
                  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions -->
                  <p class="lg-zeile__warnzeile" onclick={() => (u.offen = true)}>{warnZu[0].text}</p>
                {/if}

                {#if u.offen}
                  <!-- `is-suche` am RASTER, nicht am Feld: Die Luft über der offenen Suche
                       gilt für beide Spalten, sonst stehen Suche und Menge versetzt. -->
                  <div class="lg-zeile__edit" class:is-suche={u.suche} class:is-aus={!z.imLager}>
                    {#if !z.imLager}
                      <!-- Keine Felder für eine Zeile, die nichts bucht: Zutat
                           und Menge wären hier eine Frage ohne Wirkung. -->
                      <p class="lg-zeile__ruhig">{t('lager_abschluss_nicht_text')}</p>
                    {:else}
                      <!-- ⚠ ZutatSuche MUSS direktes Kind dieses Feldes sein.
                           Sie rollt beim Öffnen ihren ELTERNBLOCK an den oberen Rand des
                           Scrollbereichs (anDenAnfang). Lag sie in einer eigenen Hülle,
                           war das die Hülle — und Beschriftung samt „Suche schließen"
                           rollten aus dem Bild; die Maske war ohne Zurückrollen nicht mehr
                           bedienbar (Betrieb 2026-09-15). Als Kind des Feldes bleibt die
                           Beschriftungszeile oben stehen. -->
                      <div class="lg-feld lg-feld--zutat">
                        <div class="lg-lab-zeile">
                          <span class="lg-lab">{t('lager_abschluss_zutat')}</span>
                          {#if u.suche}
                            <button type="button" class="lg-link lg-link--klein" onclick={() => (u.suche = false)}>
                              {t('lager_abschluss_suche_zu')}
                            </button>
                          {/if}
                        </div>
                        {#if u.suche}
                          <ZutatSuche nurStamm onwahl={(e) => waehle(z, e)} />
                        {:else}
                          <div class="lg-zeile__zutat">
                            <span class:is-leer={!stamm && !z.anlegen}>
                              {stamm || (z.anlegen ? z.name : '—')}
                              {#if z.anlegen}<span class="lg-zeile__marke">{t('lager_abschluss_neu_kurz')}</span>{/if}
                            </span>
                            <button type="button" class="lg-link" onclick={() => (u.suche = true)}>
                              {t(z.zutatId ? 'lager_abschluss_zutat_aendern' : 'lager_abschluss_zutat_zuordnen')}
                            </button>
                          </div>
                        {/if}
                      </div>

                      <div class="lg-feld lg-feld--menge">
                        <!-- Dieselbe Beschriftungszeile wie links — gleiche Höhe, gleiche Linie. -->
                        <div class="lg-lab-zeile"><span class="lg-lab">{t('lager_abschluss_menge')}</span></div>
                        <div class="lg-zeile__menge">
                          <input class="lg-zahl" type="text" inputmode="decimal" value={u.eingabe}
                                 oninput={(e) => eingeben(z, e.currentTarget.value)}
                                 placeholder="0" aria-label={t('lager_abschluss_menge')}>
                          <button type="button" class="lg-einheit" onclick={() => einheitWechseln(z)}
                                  aria-label={t('lager_einheit_wechseln')}>{u.einheit}</button>
                        </div>
                      </div>

                      <!-- „als Zutat anlegen": Eine verbackene Zutat ohne
                           Lagerzutat ist trotzdem real verbraucht. Dass sie nie
                           eingebucht wurde, ist die Auskunft, die der negative
                           Bestand gibt (Konzept 10.3). Angelegt wird unter genau
                           diesem Zeilentitel — künftige Backtage finden sie dann
                           über den Namen, ganz ohne Alias. -->
                      <!-- Der Gegenknopf steht HIER, wo der Knopf stand — nicht
                           unten in der Fußleiste. Rückgängig gehört an den Ort
                           der Handlung, sonst sucht man es. -->
                      {#if !z.zutatId && !u.suche}
                        <div class="lg-zeile__neu">
                          {#if z.anlegen}
                            <p class="lg-zeile__ruhig">{t('lager_abschluss_anlegen_gruppe', { gruppe: z.neuGruppe })}</p>
                            <button type="button" class="lg-link" onclick={() => (z.anlegen = false)}>
                              {t('lager_abschluss_anlegen_zurueck')}
                            </button>
                          {:else}
                            <button type="button" class="lg-neu" onclick={() => anlegenAn(z)}>
                              {t('lager_abschluss_anlegen', { name: z.name })}
                            </button>
                          {/if}
                        </div>
                      {/if}
                    {/if}

                    {#if fehler[z.key]}
                      <p class="lg-zeile__fehler">{fehler[z.key]}</p>
                    {/if}

                    {#if l.hinweise.length}
                      <ul class="lg-zeile__hinweise">
                        {#each l.hinweise as h}<li class:is-warn={h.warn}>{h.text}</li>{/each}
                      </ul>
                    {/if}

                    <div class="lg-zeile__fuss">
                      {#if !z.imLager}
                        <button type="button" class="lg-link" onclick={() => fuehren(z, true)}>
                          {t('lager_abschluss_doch_fuehren')}
                        </button>
                      {:else}
                        {#if !z.planZutatId && z.zutatId && !z.anlegen}
                          <label class="lg-merken">
                            <input type="checkbox" checked={z.merken}
                                   onchange={(e) => (z.merken = e.currentTarget.checked)}>
                            <span>{t('lager_abschluss_merken', { name: z.name })}</span>
                          </label>
                        {/if}
                        <!-- Für JEDE Zeile in „Prüfen", nicht nur für die ohne
                             Zuordnung (Betreiber, 2026-09-16): Sonst bliebe das
                             Grobsalz, das sackweise im Regal steht und nie
                             gezählt wird, für immer ein Prüffall. -->
                        {#if z.gruppe === PRUEFEN}
                          <button type="button" class="lg-link" onclick={() => fuehren(z, false)}>
                            {t('lager_abschluss_nicht_fuehren')}
                          </button>
                        {/if}
                      {/if}
                      <!-- `l.zurueck`, NICHT `l.geaendert`: „nicht führen" und
                           „anlegen" haben ihren eigenen Gegenknopf. Hier hingen
                           zwei Schaltflächen nebeneinander, die dasselbe taten. -->
                      {#if l.zurueck}
                        <button type="button" class="lg-link lg-link--rechts" onclick={() => zuruecksetzen(z)}>
                          {t('lager_abschluss_zuruecksetzen')}
                        </button>
                      {/if}
                    </div>
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </section>
      {/each}
      </div>
    </div>

    <div class="lg-abs__foot">
      <div class="lg-abs__knoepfe">
        <button type="button" class="lg-zweit" onclick={onabbruch}>{t('lager_abschluss_abbrechen')}</button>
        <button type="button" class="lg-cta" onclick={bestaetigen} disabled={laeuft}
                title={ISTMAC ? '⌘ ↵' : 'Strg ↵'}>
          {t(buchungen.length ? 'lager_abschluss_cta' : 'lager_abschluss_cta_leer')}
        </button>
      </div>
    </div>

  </div>
</div>

<style>
  /* Schmal eine Fläche am unteren Rand, SO HOCH WIE IHR INHALT (Muster des
     Korrektur-Sheets), ab 600 px Container-Breite ein Dialog. Bildschirmhoch
     wie die Eingangsmaske stand ohne Konflikte — Journal zugeklappt — eine
     Karte oben und darunter leerer Grund bis zum Knopf (2026-09-15). Mit
     aufgeklapptem Journal wächst die Fläche bis fast an den oberen Rand. */
  .lg-host { position: fixed; inset: 0; z-index: 60; display: flex; container-type: inline-size; }
  .lg-scrim { position: absolute; inset: 0; background: rgba(41, 37, 36, 0.30); }
  .lg-abs {
    position: relative; flex: 0 1 auto; width: 100%; margin-top: auto;
    max-height: calc(100% - env(safe-area-inset-top, 0px) - 24px);
    min-height: 0; display: flex; flex-direction: column;
    background: #f0ede9; color: var(--lg-ink);
    border-radius: 16px 16px 0 0; overflow: hidden;
    box-shadow: 0 -12px 40px -18px rgba(28, 25, 23, 0.45);
  }

  .lg-abs__head {
    flex-shrink: 0; display: grid; grid-template-columns: 1fr 44px; align-items: center;
    gap: 8px; padding: 12px 10px 10px 20px;
    background: rgba(245, 243, 240, 0.94); border-bottom: 1px solid var(--lg-line);
  }
  .lg-abs__kopftext { min-width: 0; }
  .lg-abs__titel { font-size: 17px; font-weight: 700; letter-spacing: -0.015em; }
  .lg-abs__unter {
    margin-top: 2px; font-size: 12.5px; color: var(--lg-ink-mittel);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .lg-abs__x {
    width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;
    border: 0; border-radius: 10px; background: none; color: var(--lg-ink-soft); cursor: pointer;
  }
  .lg-abs__x:hover { background: rgba(41, 37, 36, 0.06); color: var(--lg-ink); }
  .lg-abs__x:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }

  .lg-abs__leib { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 4px 0 24px; }
  .lg-abs__hinweis { margin: 14px 20px 0; font-size: 13px; line-height: 1.5; color: var(--lg-ink-soft); }

  /* ── Übersicht: was beim Abschluss mit dem Lager passiert ─────────────── */
  .lg-uebersicht {
    margin: 16px 16px 0; padding: 16px 18px;
    background: var(--lg-card); border: 1px solid var(--lg-line); border-radius: 14px;
  }
  .lg-uebersicht__titel { margin: 0; font-size: 16px; font-weight: 700; letter-spacing: -0.01em; }
  .lg-uebersicht__text { margin: 4px 0 0; font-size: 13.5px; line-height: 1.5; color: var(--lg-ink-soft); }
  .lg-uebersicht__werte { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
  .lg-wert {
    display: inline-flex; align-items: center; min-height: 32px; padding: 0 12px;
    border-radius: 999px; background: var(--lg-tint); border: 1px solid var(--lg-line);
    font: inherit; font-size: 13px; font-weight: 700; color: var(--lg-ink);
    font-variant-numeric: tabular-nums;
  }
  .lg-wert--pruefen {
    background: var(--lg-hinweis-bg); border-color: var(--lg-hinweis-edge); color: var(--lg-hinweis-ink);
    cursor: pointer;
  }
  .lg-wert--pruefen:hover { border-color: var(--lg-hinweis-strich); }
  .lg-wert--pruefen:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }
  .lg-wert--ok { background: var(--lg-ein-bg); border-color: transparent; color: var(--lg-ein-ink); }

  /* ── Der Kopf des einklappbaren Journals ─────────────────────────────── */
  .lg-journal-kopf {
    display: flex; align-items: center; gap: 10px; width: 100%; box-sizing: border-box;
    min-height: 52px; margin-top: 12px; padding: 0 14px 0 20px;
    border: 0; background: none; font: inherit; color: inherit; cursor: pointer; text-align: left;
  }
  .lg-journal-kopf:hover { background: rgba(41, 37, 36, 0.04); }
  .lg-journal-kopf:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }
  .lg-journal-kopf__titel {
    font-size: 12px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase;
    color: var(--lg-ink-soft);
  }
  .lg-journal-kopf__anzahl { font-size: 11.5px; color: var(--lg-ink-mute); font-variant-numeric: tabular-nums; }
  .lg-journal-kopf__tu { margin-left: auto; font-size: 12.5px; font-weight: 600; color: var(--lg-ink-soft); }
  .lg-journal-kopf__pfeil { color: var(--lg-ink-mute); transition: transform 0.15s; }
  .lg-journal-kopf[aria-expanded="true"] .lg-journal-kopf__pfeil { transform: rotate(180deg); }
  .lg-journal-note { margin: 0 20px; font-size: 12px; line-height: 1.5; color: var(--lg-ink-mute); }

  /* ── Gruppen: dieselbe Sprache wie die Bestandsliste ─────────────────── */
  .lg-gruppe { margin-top: 18px; }
  .lg-gruppe__kopf { display: flex; align-items: baseline; gap: 10px; padding: 0 20px 8px; }
  .lg-gruppe__name {
    font-size: 12px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--lg-ink-soft);
  }
  .lg-gruppe__anzahl { font-size: 11.5px; color: var(--lg-ink-mute); font-variant-numeric: tabular-nums; }
  /* Amber, nicht Rose: „Prüfen" ist ein Hinweis, keine Gefahr (--lg-hinweis-*
     in Lager.svelte, dieselben Werte wie `.notice--warn`). */
  .lg-gruppe.is-pruefen .lg-gruppe__name { color: var(--lg-hinweis-ink); }
  /* Grau, nicht Amber: Hier ist nichts zu tun — das ist der ganze Punkt. */
  .lg-gruppe.is-nicht .lg-gruppe__name { color: var(--lg-ink-mute); }
  .lg-gruppe__kopf--knopf {
    width: 100%; box-sizing: border-box; min-height: 44px; padding-top: 4px;
    border: 0; background: none; font: inherit; color: inherit; text-align: left; cursor: pointer;
  }
  .lg-gruppe__kopf--knopf:hover .lg-gruppe__name { color: var(--lg-ink-soft); }
  .lg-gruppe__kopf--knopf:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }
  .lg-gruppe__pfeil { margin-left: auto; color: var(--lg-ink-mute); transition: transform 0.15s; }
  .lg-gruppe__kopf--knopf[aria-expanded="true"] .lg-gruppe__pfeil { transform: rotate(180deg); }
  .lg-karte {
    background: var(--lg-card); border-top: 1px solid var(--lg-line); border-bottom: 1px solid var(--lg-line);
  }

  /* ── Zeile ─────────────────────────────────────────────────────────────── */
  .lg-zeile { position: relative; border-bottom: 1px solid var(--lg-line-fein); }
  .lg-zeile:last-child { border-bottom: 0; }
  /* Der Strich ist ein eigenes Element ÜBER den Kindern. Als inset-Schatten der
     Zeile lag er unter ihnen, und die Hover-Fläche des Kopfes deckte ihn zu. */
  .lg-zeile.is-warn::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
    background: var(--lg-hinweis-strich); z-index: 1; pointer-events: none;
  }

  .lg-zeile__kopf {
    width: 100%; box-sizing: border-box; min-height: 60px;
    display: grid; grid-template-columns: minmax(0, 1fr) auto 20px;
    grid-template-areas: "name wert pfeil" "stamm wert pfeil";
    align-items: center; column-gap: 12px; row-gap: 2px;
    padding: 10px 14px 10px 20px; border: 0; background: none; font: inherit;
    text-align: left; color: inherit; cursor: pointer;
  }
  /* Zugeklappt ist die ganze Zeile die Fläche — samt Hinweiszeile unter dem
     Kopf. Nur am Kopf endete die Hover-Fläche mitten in der Zeile. */
  .lg-zeile:not(.is-offen):hover,
  .lg-zeile.is-offen .lg-zeile__kopf:hover { background: var(--lg-tint); }
  .lg-zeile:not(.is-offen) .lg-zeile__warnzeile { cursor: pointer; }
  .lg-zeile__kopf:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }
  .lg-zeile__name {
    grid-area: name; min-width: 0; display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px;
    font-size: 14.5px; font-weight: 600; letter-spacing: -0.005em;
  }
  .lg-zeile__stamm {
    grid-area: stamm; min-width: 0; font-size: 12px; color: var(--lg-ink-mittel);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .lg-zeile__stamm.is-leer { color: var(--lg-hinweis-ink); font-weight: 600; }
  .lg-zeile__wert {
    grid-area: wert; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .lg-zeile.is-aus .lg-zeile__wert { color: var(--lg-ink-mute); font-weight: 600; }
  .lg-zeile__pfeil { grid-area: pfeil; color: var(--lg-ink-mute); transition: transform 0.15s; }
  .lg-zeile.is-offen .lg-zeile__pfeil { transform: rotate(180deg); }
  .lg-zeile__marke {
    white-space: nowrap; padding: 1px 7px; border-radius: 999px;
    background: var(--lg-tint); border: 1px solid var(--lg-line);
    font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
    color: var(--lg-ink-soft);
  }
  .lg-zeile__warnzeile {
    margin: -4px 0 0; padding: 0 14px 10px 20px;
    font-size: 12px; font-weight: 600; color: var(--lg-hinweis-ink);
  }

  .lg-zeile__edit {
    display: grid; grid-template-columns: minmax(0, 1fr); gap: 12px;
    padding: 2px 20px 16px;
  }
  .lg-lab {
    display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--lg-ink-mute); margin-bottom: 6px;
  }
  .lg-zeile__zutat {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    min-height: 52px; box-sizing: border-box; padding: 0 6px 0 12px;
    background: var(--lg-tint); border: 1px solid var(--lg-line); border-radius: 10px;
    font-size: 14px; font-weight: 600;
  }
  .lg-zeile__zutat .is-leer { color: var(--lg-ink-mute); font-weight: 400; }
  .lg-lab-zeile {
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    min-height: 28px; margin-bottom: 4px;
  }
  .lg-lab-zeile .lg-lab { margin-bottom: 0; }
  /* Die Suche rollt ihr Feld bündig an den oberen Rand. Ohne Luft klebte die
     Beschriftung an der Kante des Scrollbereichs. Beide Spalten bekommen sie,
     damit Suche und Menge auf einer Linie bleiben. */
  .lg-zeile__edit.is-suche > .lg-feld { padding-top: 10px; }
  .lg-feld--zutat > :global(.lg-such) { width: 100%; }

  .lg-zeile__menge { display: grid; grid-template-columns: 132px 56px; gap: 6px; }
  /* 52 px wie das Suchfeld der ZutatSuche — sonst stehen beide Spalten
     unterschiedlich hoch, sobald links gesucht wird. */
  .lg-zahl {
    height: 52px; box-sizing: border-box; padding: 0 12px; min-width: 0; text-align: right;
    background: var(--lg-card); border: 1px solid #d6d3d1; border-radius: 10px;
    font: inherit; font-size: 17px; font-weight: 700; color: var(--lg-ink);
    font-variant-numeric: tabular-nums;
  }
  .lg-zahl:focus { outline: none; border-color: var(--lg-ink); box-shadow: 0 0 0 3px rgba(41, 37, 36, 0.07); }
  /* Gepunktet unterstrichen wie in Eingangsmaske und Korrektur-Sheet. */
  .lg-einheit {
    height: 52px; border: 0; border-radius: 10px; background: none; font: inherit;
    font-size: 13px; font-weight: 700; color: var(--lg-ink-soft); cursor: pointer;
    text-decoration: underline dotted; text-decoration-color: var(--lg-ink-mute);
    text-underline-offset: 4px;
  }
  .lg-einheit:hover { background: rgba(41, 37, 36, 0.06); color: var(--lg-ink); }
  .lg-einheit:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }

  .lg-zeile__hinweise { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
  .lg-zeile__hinweise li { font-size: 12.5px; line-height: 1.45; color: var(--lg-ink-soft); }
  .lg-zeile__hinweise li.is-warn {
    padding: 8px 12px; border-radius: 10px;
    background: var(--lg-hinweis-bg); border: 1px solid var(--lg-hinweis-edge); color: var(--lg-hinweis-ink);
  }

  /* Eine Zeile, die nichts bucht, bekommt keine Felder — nur einen Satz. */
  .lg-zeile__ruhig { margin: 0; font-size: 12.5px; line-height: 1.45; color: var(--lg-ink-mittel); }
  /* Als direktes Kind über beide Spalten: Der Satz ersetzt Zutat UND Menge. */
  .lg-zeile__edit > .lg-zeile__ruhig { grid-column: 1 / -1; }
  .lg-zeile.is-aus .lg-zeile__name { color: var(--lg-ink-soft); }

  /* „als Zutat anlegen": ein eigener Knopf, keine neunfache Chip-Reihe. Die
     Warengruppe kommt aus dem Katalog und steht als Satz darunter — ändern
     lässt sie sich im Lager. Die Maske handelt vom Abgang, nicht von der
     Stammpflege (Betreiber, 2026-09-16). */
  .lg-zeile__neu {
    grid-column: 1 / -1;
    display: flex; flex-wrap: wrap; align-items: center; gap: 0 12px;
  }
  .lg-neu {
    min-height: 44px; padding: 0 14px; box-sizing: border-box;
    border: 1px dashed var(--lg-ink-mute); border-radius: 10px; background: var(--lg-card);
    font: inherit; font-size: 13.5px; font-weight: 700; color: var(--lg-ink-soft); cursor: pointer;
    text-align: left;
  }
  .lg-neu:hover { border-color: var(--lg-ink); border-style: solid; color: var(--lg-ink); }
  .lg-neu:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }

  .lg-zeile__fehler {
    grid-column: 1 / -1; margin: 0; padding: 8px 12px; border-radius: 10px;
    background: var(--lg-fehlt-bg); border: 1px solid var(--lg-fehlt-edge); color: var(--lg-fehlt-ink);
    font-size: 12.5px; line-height: 1.45; font-weight: 600;
  }

  .lg-zeile__fuss { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; }
  .lg-link--rechts { margin-left: auto; }
  .lg-merken { display: flex; align-items: center; gap: 8px; min-height: 44px; font-size: 13px; color: var(--lg-ink-soft); cursor: pointer; }
  .lg-merken input { width: 18px; height: 18px; accent-color: var(--lg-ink); }

  .lg-link {
    min-height: 40px; padding: 0 10px; border: 0; border-radius: 8px; background: none;
    font: inherit; font-size: 13px; font-weight: 700; color: var(--lg-ink-soft); cursor: pointer;
    text-decoration: underline; text-decoration-color: var(--lg-line); text-underline-offset: 4px;
  }
  .lg-link:hover { background: rgba(41, 37, 36, 0.06); color: var(--lg-ink); }
  .lg-link:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }
  .lg-link--klein { min-height: 28px; margin-right: -10px; font-size: 12px; }

  /* ── Fußleiste ─────────────────────────────────────────────────────────── */
  .lg-abs__foot {
    flex-shrink: 0; display: grid; gap: 10px;
    padding: 12px 16px calc(16px + env(safe-area-inset-bottom, 0px));
    background: rgba(245, 243, 240, 0.94); border-top: 1px solid var(--lg-line);
  }

  /* Schmal nur EIN Knopf: Neben „Abbrechen" brach „Abschließen und abbuchen"
     auf 390 px zweizeilig um. Abbrechen bleibt über das Kreuz im Kopf. */
  .lg-abs__knoepfe { display: grid; grid-template-columns: 1fr; gap: 8px; }
  .lg-abs__knoepfe .lg-zweit { display: none; }
  .lg-zweit {
    min-height: 54px; padding: 0 16px; border: 1px solid #d6d3d1; border-radius: 10px;
    background: var(--lg-card); font: inherit; font-size: 14.5px; font-weight: 600;
    color: var(--lg-ink-soft); cursor: pointer;
  }
  .lg-zweit:hover { color: var(--lg-ink); border-color: var(--lg-ink-mute); }
  .lg-zweit:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }
  .lg-cta {
    min-height: 54px; padding: 0 18px; border: 0; border-radius: 10px;
    background: var(--lg-accent); color: #fff; box-shadow: 0 6px 16px -8px rgba(192, 90, 62, 0.5);
    font: inherit; font-size: 15.5px; font-weight: 700; letter-spacing: -0.01em; cursor: pointer;
  }
  .lg-cta:hover:not(:disabled) { background: var(--lg-accent-hover); }
  .lg-cta:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }
  .lg-cta:disabled { background: #e7e5e4; color: var(--lg-ink-soft); box-shadow: none; cursor: default; }

  /* ── Ab 600 px: Dialog, und die Zeile wird zum Raster ─────────────────── */
  @container (min-width: 600px) {
    .lg-abs {
      position: relative; flex: 0 1 auto; margin: auto; width: 720px;
      max-height: min(88%, 820px); border: 1px solid var(--lg-line); border-radius: 16px;
      box-shadow: 0 24px 48px -24px rgba(28, 25, 23, 0.45); overflow: hidden;
    }
    .lg-abs__head { padding: 12px 12px 12px 24px; }
    .lg-gruppe__kopf { padding: 0 24px 8px; }
    .lg-abs__hinweis { margin: 16px 24px 0; }
    .lg-uebersicht { margin: 18px 24px 0; padding: 18px 20px; }
    .lg-journal-kopf { padding: 0 16px 0 24px; }
    .lg-journal-note { margin: 0 24px; }
    /* Rezeptname | Zutat im Lager | Menge — in EINER Zeile. */
    .lg-zeile__kopf {
      grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) 96px 20px;
      grid-template-areas: "name stamm wert pfeil";
      padding: 10px 16px 10px 24px;
    }
    .lg-zeile__wert { text-align: right; }
    .lg-zeile__warnzeile { padding: 0 16px 10px 24px; }
    .lg-zeile__edit { grid-template-columns: minmax(0, 1fr) 200px; column-gap: 20px; padding: 2px 24px 18px; }
    .lg-zeile__hinweise, .lg-zeile__fuss { grid-column: 1 / -1; }
    .lg-abs__foot { justify-items: end; padding: 14px 24px; }
    .lg-abs__knoepfe { grid-template-columns: auto auto; }
    .lg-abs__knoepfe .lg-zweit { display: block; }
  }
</style>
